// src/Components/Documents/ImageLightbox.js
import React, { useEffect } from 'react';
import { downloadFile } from '../../utils/downloadUtils';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon
} from '@mui/icons-material';

/**
 * ImageLightbox Component
 *
 * A fullscreen image viewer with navigation, download, and zoom capabilities
 *
 * @param {boolean} open - Whether the lightbox is open
 * @param {function} onClose - Callback when lightbox closes
 * @param {Array} images - Array of image objects [{url, fileName, ...}]
 * @param {number} currentIndex - Current image index
 * @param {function} onNavigate - Callback when navigating (receives new index)
 */
const ImageLightbox = ({
  open,
  onClose,
  images = [],
  currentIndex = 0,
  onNavigate
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [zoom, setZoom] = React.useState(1);

  const currentImage = images[currentIndex];
  const hasMultipleImages = images.length > 1;

  // Reset zoom when image changes
  useEffect(() => {
    setZoom(1);
  }, [currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        onNavigate(currentIndex - 1);
      } else if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
        onNavigate(currentIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, currentIndex, images.length, onClose, onNavigate]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      onNavigate(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < images.length - 1) {
      onNavigate(currentIndex + 1);
    }
  };

  const handleDownload = async () => {
    if (!currentImage) return;
    const filename = currentImage.fileName || currentImage.title || currentImage.name || 'image';
    const url = currentImage.fileUrl || currentImage.downloadURL;
    const storagePath = currentImage.storagePath;
    await downloadFile(url, filename, storagePath);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  if (!currentImage) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          m: 0,
          p: 0,
        }
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Header Bar */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 2,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Image info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'white',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {currentImage.fileName || currentImage.title || 'Untitled'}
            </Typography>
            {hasMultipleImages && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {currentIndex + 1} of {images.length}
              </Typography>
            )}
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isMobile && (
              <>
                <IconButton
                  onClick={handleZoomOut}
                  disabled={zoom <= 0.5}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                  }}
                >
                  <ZoomOutIcon />
                </IconButton>
                <IconButton
                  onClick={handleZoomIn}
                  disabled={zoom >= 3}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                  }}
                >
                  <ZoomInIcon />
                </IconButton>
              </>
            )}
            <IconButton
              onClick={handleDownload}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              onClick={onClose}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Image Container */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Navigation Buttons */}
          {hasMultipleImages && (
            <>
              {/* Previous Button */}
              <IconButton
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                sx={{
                  position: 'absolute',
                  left: isMobile ? 8 : 24,
                  zIndex: 1,
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(0,0,0,0.3)',
                    color: 'rgba(255,255,255,0.3)'
                  }
                }}
                size={isMobile ? 'medium' : 'large'}
              >
                <NavigateBeforeIcon fontSize={isMobile ? 'medium' : 'large'} />
              </IconButton>

              {/* Next Button */}
              <IconButton
                onClick={handleNext}
                disabled={currentIndex === images.length - 1}
                sx={{
                  position: 'absolute',
                  right: isMobile ? 8 : 24,
                  zIndex: 1,
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(0,0,0,0.3)',
                    color: 'rgba(255,255,255,0.3)'
                  }
                }}
                size={isMobile ? 'medium' : 'large'}
              >
                <NavigateNextIcon fontSize={isMobile ? 'medium' : 'large'} />
              </IconButton>
            </>
          )}

          {/* Image */}
          <Box
            component="img"
            src={currentImage.fileUrl}
            alt={currentImage.fileName || 'Image'}
            sx={{
              maxWidth: zoom === 1 ? '90%' : 'none',
              maxHeight: zoom === 1 ? '90%' : 'none',
              width: zoom !== 1 ? `${zoom * 100}%` : 'auto',
              height: 'auto',
              objectFit: 'contain',
              userSelect: 'none',
              transition: 'transform 0.2s ease',
              cursor: zoom > 1 ? 'grab' : 'default'
            }}
          />
        </Box>

        {/* Mobile zoom info */}
        {isMobile && zoom !== 1 && (
          <Box sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'rgba(0,0,0,0.7)',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: 1
          }}>
            <Typography variant="caption">
              Zoom: {Math.round(zoom * 100)}%
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImageLightbox;
